import { UserPlusIcon } from '@heroicons/react/24/outline';
import { DynamicForm, Modal } from '@src/components';
import { UserSchema } from '@src/formSchemas/user/UserSchema';
import { errorHandler, useDynamicFormState } from '@src/hooks';
import { useAddUserMutation } from '@src/services/users/addUsers/addUserApiSlice';
import { Button, message } from 'antd';
import { useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
interface NewUserModalProps {
    open: boolean;
    onClose: () => void;
}
const NewUserModal = ({ open, onClose }: NewUserModalProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        formRef,
        isDirty,
        isValid,
        isValidating,
        handleDataChange,
        handleSubmit,
    } = useDynamicFormState();
    const [account, setAccount] = useState<
        { label: string; value: string } | undefined
    >();

    const [addUser, { isLoading: isAddUserLoading }] = useAddUserMutation();
    const onSubmit = async (formData: FieldValues) => {
        try {
            await addUser({
                roleId: formData.role?.value,
                email: formData.email,
                accountId: formData.account.value,
            }).unwrap();
            message.success({
                content: t(`notification.success.create`, {
                    name: t('pages.users.forms.title'),
                }),
                className: 'response-success',
                type: 'success',
            });
            onClose();
        } catch (error: any) {
            message.error({
                content: t(errorHandler(error)),
                className: 'response-error',
                type: 'error',
            });
        }
    };
    const addNewRole = () => {
        navigate('/dashboard/user-management/roles/new');
    };

    const handleAccountChange = (account: { label: string; value: string }) => {
        setAccount(account);
    };
    return (
        <Modal
            title={t('pages.users.forms.title')}
            headerIcon={
                <UserPlusIcon
                    width={20}
                    height={20}
                    className="fill-secondary"
                />
            }
            width={507}
            isOpen={open}
            onClose={onClose}
            content={
                <DynamicForm
                    useFormOptions={{
                        mode: 'onChange',
                        reValidateMode: 'onChange',
                    }}
                    ref={formRef}
                    formKeyName="new-user-form"
                    onHandleSubmit={onSubmit}
                    containerClassNames="grid gap-4 [&_label]:mb-2"
                    formRows={UserSchema({
                        t,
                        accountId: account ? account.value : '',
                        onAccountChange: handleAccountChange,
                        addNewRole,
                    })}
                    hasDataChanged={handleDataChange}
                />
            }
            footer={
                <div className="w-full flex justify-between text-center space-x-2">
                    <Button
                        size="large"
                        className="btn-secondary w-full justify-center"
                        onClick={onClose}
                        disabled={isAddUserLoading}
                    >
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        size="large"
                        htmlType="button"
                        className="btn-primary w-full text-center justify-center"
                        loading={isAddUserLoading}
                        onClick={() => handleSubmit(onSubmit)}
                        disabled={!isDirty || !isValid || isValidating}
                    >
                        {t('pages.users.buttons.sendInvitationEmail')}
                    </Button>
                </div>
            }
        />
    );
};

export default NewUserModal;
